import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { Contact } from '../../types';
import { leadsService } from '../../data/leadsService';

interface ContactState {
  contacts: Contact[];
  loading: boolean;
  error: string | null;
  totalCounts: number;
  summary: {
    total: number;
    active: number;
    pending: number;
    inactive: number;
  };
  pagination: {
    page: number;
    limit: number;
    pages: number;
    total: number;
  };
}

const initialState: ContactState = {
  contacts: [],
  loading: false,
  error: null,
  totalCounts: 0,
  summary: {
    total: 0,
    active: 0,
    pending: 0,
    inactive: 0,
  },
  pagination: {
    page: 1,
    limit: 12,
    pages: 1,
    total: 0,
  },
};

// Async Thunks
export const fetchContacts = createAsyncThunk(
  'contacts/fetchContacts',
  async (params: { page?: number; limit?: number; search?: string; status?: string } = {}, { rejectWithValue }) => {
    try {
      const response = await leadsService.getContacts(params.page, params.limit, params.search, params.status);
      if (response.success && response.data) {
        return response.data;
      }
      return rejectWithValue(response.error?.message || 'Failed to fetch contacts');
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch contacts');
    }
  }
);

export const createContact = createAsyncThunk(
  'contacts/createContact',
  async (data: Partial<Contact>, { rejectWithValue }) => {
    try {
      const response = await leadsService.createContact(data);
      if (response.success && response.data) {
        return response.data;
      }
      return rejectWithValue(response.error?.message || 'Failed to create contact');
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create contact');
    }
  }
);

export const updateContact = createAsyncThunk(
  'contacts/updateContact',
  async ({ id, data }: { id: string; data: Partial<Contact> }, { rejectWithValue }) => {
    try {
      const response = await leadsService.updateContact(id, data);
      if (response.success && response.data) {
        return response.data;
      }
      return rejectWithValue(response.error?.message || 'Failed to update contact');
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update contact');
    }
  }
);

export const deleteContact = createAsyncThunk(
  'contacts/deleteContact',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await leadsService.deleteContact(id);
      if (response.success) {
        return id;
      }
      return rejectWithValue(response.error?.message || 'Failed to delete contact');
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete contact');
    }
  }
);

const contactSlice = createSlice({
  name: 'contacts',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch Contacts
    builder.addCase(fetchContacts.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchContacts.fulfilled, (state, action) => {
      state.loading = false;
      state.contacts = action.payload.contacts;
      state.totalCounts = action.payload.pagination.total;
      state.pagination = action.payload.pagination;
      if (action.payload.summary) {
        state.summary = action.payload.summary;
      }
    });
    builder.addCase(fetchContacts.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Create Contact
    builder.addCase(createContact.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createContact.fulfilled, (state, action) => {
      state.loading = false;
      state.contacts.unshift(action.payload);
      state.totalCounts += 1;
    });
    builder.addCase(createContact.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Update Contact
    builder.addCase(updateContact.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.contacts.findIndex(c => c.id === action.payload.id);
      if (index !== -1) {
        state.contacts[index] = action.payload;
      }
    });

    // Delete Contact
    builder.addCase(deleteContact.fulfilled, (state, action) => {
      state.loading = false;
      state.contacts = state.contacts.filter(c => c.id !== action.payload);
      state.totalCounts -= 1;
    });
  }
});

export const { setLoading, setError, clearError } = contactSlice.actions;

// Selectors
export const selectContacts = (state: { contacts: ContactState }) => state.contacts.contacts;
export const selectContactLoading = (state: { contacts: ContactState }) => state.contacts.loading;
export const selectContactError = (state: { contacts: ContactState }) => state.contacts.error;
export const selectContactPagination = (state: { contacts: ContactState }) => state.contacts.pagination;
export const selectContactTotalCounts = (state: { contacts: ContactState }) => state.contacts.totalCounts;
export const selectContactSummary = (state: { contacts: ContactState }) => state.contacts.summary;

export default contactSlice.reducer;
